import {
  CRMChannel,
  CRMConversationStatus,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { instagramConversations$ } from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { emailConversations$ } from "@jugl-web/rest-api/integrations/hooks/useEmailConversations";
import { twilioConversations$ } from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { webChatBotConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWebChatBotConversations";
import { whatsappConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useCallback, useMemo } from "react";
import { ConversationStatusDialogVariant } from "../components/ClientSupportChat/components/ChatTopBar/components/ConversationStatusDialog";
import { useClientSupportPageContext } from "../providers/ClientSupportPageProvider/useClientSupportPageContext";

export const useUpdateConversationStatus = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const {
    selectedConversation,
    refetchTrigger$,
    selectConversation,
    selectedChannel,
  } = useClientSupportPageContext();

  const [
    updateConversationStatus,
    { isLoading: isUpdatingConversationStatus },
  ] = integrationsApi.useUpdateConversationStatusMutation();

  const contactName = useMemo(
    () =>
      selectedConversation
        ? selectedConversation.contact_info?.name || selectedConversation.name
        : "",
    [selectedConversation]
  );

  const showConversationStatusToast = useCallback(
    (variant: ConversationStatusDialogVariant) => {
      if (variant === "close") {
        toast(
          <span>
            {t(
              {
                id: "client-support-page.conversation-was-closed",
                defaultMessage: "Conversation with {name} was closed",
              },
              {
                name: <span className="font-semibold">{contactName}</span>,
              }
            )}
          </span>
        );
        return;
      }

      toast(
        <span>
          {t(
            {
              id: "client-support-page.conversation-was-reopened",
              defaultMessage: "Conversation with {name} was reopened",
            },
            {
              name: <span className="font-semibold">{contactName}</span>,
            }
          )}
        </span>
      );
    },
    [contactName, t, toast]
  );

  const handleUpdateConversationStatus = useCallback(
    async (variant: ConversationStatusDialogVariant) => {
      if (!selectedConversation) {
        return;
      }

      const status =
        variant === "close"
          ? CRMConversationStatus.closed
          : CRMConversationStatus.active;

      const response = await updateConversationStatus({
        entityId,
        channel: selectedChannel,
        conv_id: selectedConversation.conv_id,
        status,
      });

      if (!response || !("data" in response)) {
        return;
      }

      const conversationId = selectedConversation.conv_id;
      const channel = selectedChannel;

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          showConversationStatusToast(variant);

          switch (channel) {
            case CRMChannel.whatsapp:
              whatsappConversations$.next({ type: "remove", conversationId });
              break;
            case CRMChannel.instagram:
              instagramConversations$.next({ type: "remove", conversationId });
              break;
            case CRMChannel.chatbot:
              webChatBotConversations$.next({ type: "remove", conversationId });
              break;
            case CRMChannel.twilio:
              twilioConversations$.next({ type: "remove", conversationId });
              break;
            case CRMChannel.email:
              emailConversations$.next({ type: "remove", conversationId });
              break;
            default:
              break;
          }

          refetchTrigger$.next();
          selectConversation(undefined);
          resolve();
        }, 10);
      });
    },
    [
      entityId,
      refetchTrigger$,
      selectConversation,
      selectedChannel,
      selectedConversation,
      showConversationStatusToast,
      updateConversationStatus,
    ]
  );

  return {
    handleUpdateConversationStatus,
    isUpdatingConversationStatus,
  };
};
